import { Experiment } from '@amplitude/experiment-js-client';

import { RuntimeControlFeatures } from './runtime-control.const';
import { extractRuntimeControlConfigs } from './runtime-control.helpers';
import {
  ExperimentClient,
  RuntimeControlEnabledFlag,
  RuntimeControlsConfig,
} from './runtime-control.types';

/**
 * Provides a shared Amplitude Experiment client instance for runtime controls.
 * Must be initialised once when the application loads via `initialise()`.
 */
export class RuntimeControls {
  private static experimentClient: ExperimentClient | null = null;
  private static config: RuntimeControlsConfig | null = null;

  /**
   * initialises the runtime controls with deployment key and app configuration.
   * Should be called once when the application loads.
   *
   * @param config Initialization settings for deployment, environment, and app.
   */
  static async initialise(
    config: RuntimeControlsConfig,
  ): Promise<undefined | ExperimentClient> {
    if (this.experimentClient) {
      console.debug('RuntimeControls already initialised. Skipping re-initialization.');
      return this.experimentClient;
    }
    if (!config.deploymentKey) {
      console.debug(
        'RuntimeControls initialization missing deploymentKey. Runtime controls will not function without it.',
      );
      return;
    }

    this.config = config;

    this.experimentClient = Experiment.initializeWithAmplitudeAnalytics(
      config.deploymentKey,
      {
        automaticFetchOnAmplitudeIdentityChange: true,
      },
    );
    await this.experimentClient?.start();
    return this.experimentClient;
  }

  /**
   * Resolves an `enabled` boolean flag for the provided runtime-control feature.
   *
   * Returns `false` when the runtime controls are not initialised, when
   * `instanceName` is missing, or when the payload does not contain `enabled`.
   *
   * @param feature Runtime-control feature key.
   */
  static isEnabled(feature: RuntimeControlFeatures): boolean {
    const data = this.getConfig<RuntimeControlEnabledFlag>(feature);
    return !!data?.enabled;
  }

  /**
   * Resolves a feature payload into the app/environment specific config.
   *
   * Returns `undefined` when runtime controls are not initialised, when
   * `instanceName` is missing, or when the target payload path does not exist.
   *
   * @template TConfig Expected config shape for the feature payload.
   * @param feature Runtime-control feature key.
   */
  static getConfig<TConfig extends Record<string, unknown>>(
    feature: RuntimeControlFeatures,
  ): TConfig | undefined {
    if (!this.experimentClient) {
      console.debug(
        'RuntimeControls getConfig called before initialization. Returning undefined.',
      );
      return undefined;
    }

    if (!this.config?.instanceName) {
      console.debug(
        'RuntimeControls getConfig missing instanceName in config. Returning undefined.',
      );
      return undefined;
    }

    const variant = this.experimentClient.variant(feature);
    return extractRuntimeControlConfigs<TConfig>({
      instanceName: this.config.instanceName,
      appName: this.config.appName,
      configs: variant?.payload,
    });
  }
}
